import { Link } from "react-router-dom";
import { useState } from "react";
import { getReadingTime } from "@/lib/readingTime";

function stripHtml(html) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

function CardImage({ src, alt, className = "", category = "" }) {
  const [failed, setFailed] = useState(false);
  const themes = [
    { bg: "bg-[#0f2b4c]", accent: "text-sky-400/60", pattern: "bg-[radial-gradient(circle_at_30%_40%,rgba(56,189,248,0.08)_0%,transparent_60%)]" },
    { bg: "bg-[#1a3a5c]", accent: "text-emerald-400/60", pattern: "bg-[radial-gradient(circle_at_70%_30%,rgba(52,211,153,0.08)_0%,transparent_60%)]" },
    { bg: "bg-[#162d4a]", accent: "text-amber-400/60", pattern: "bg-[radial-gradient(circle_at_50%_60%,rgba(251,191,36,0.08)_0%,transparent_60%)]" },
    { bg: "bg-[#1e3554]", accent: "text-violet-400/60", pattern: "bg-[radial-gradient(circle_at_20%_70%,rgba(167,139,250,0.08)_0%,transparent_60%)]" },
  ];
  const t = themes[(alt || "").length % themes.length];
  if (!src || failed) {
    return (
      <div className={`w-full h-full ${t.bg} ${t.pattern} flex flex-col items-center justify-center gap-4 p-6 ${className}`}>
        <img src="https://customer-assets.emergentagent.com/job_1b33c282-eb60-405a-be45-cbe3617d967c/artifacts/rhqju155_OFAlycee_center_logo_blue.png" alt="OFALycee" className="h-8 opacity-30" />
        {category && <span className={`text-xs font-medium tracking-widest uppercase ${t.accent}`}>{category}</span>}
      </div>
    );
  }
  return <img src={src} alt={alt} className={`w-full h-full object-cover object-center article-card-image ${className}`} loading="lazy" onError={() => setFailed(true)} />;
}

export default function ArticleCard({ article, variant = "default" }) {
  const category = article.category_details?.[0];
  const author = article.author;
  const date = article.publication_date
    ? new Date(article.publication_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : "";
  const readingTime = getReadingTime(article.content);

  if (variant === "featured") {
    return (
      <article className="group" data-testid={`article-card-featured-${article.slug}`}>
        <Link to={`/article/${article.slug}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-7 overflow-hidden aspect-[2/1] bg-muted/30 rounded-sm">
            <CardImage src={article.featured_image} alt={article.title} category={category?.name} />
          </div>
          <div className="lg:col-span-5 flex flex-col justify-center">
            {category && <span className="category-pill text-primary mb-4">{category.name}</span>}
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl leading-tight mb-4 group-hover:text-primary transition-colors">
              {article.title}
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6 line-clamp-3">{stripHtml(article.excerpt)}</p>
            <div className="text-sm text-muted-foreground flex items-center gap-3">
              <span>{date}</span>
              {readingTime > 0 && <span data-testid="reading-time">· {readingTime} min de lecture</span>}
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group article-card" data-testid={`article-card-${article.slug}`}>
      <Link to={`/article/${article.slug}`}>
        <div className="overflow-hidden aspect-[2/1] mb-6 bg-muted/30 rounded-sm">
          <CardImage src={article.featured_image} alt={article.title} category={category?.name} />
        </div>
        <div className="space-y-3">
          {category && <span className="category-pill text-primary">{category.name}</span>}
          <h3 className="font-heading text-xl md:text-2xl leading-snug group-hover:text-primary transition-colors">{article.title}</h3>
          <p className="text-muted-foreground text-sm md:text-base leading-relaxed line-clamp-2">{stripHtml(article.excerpt)}</p>
          <div className="text-sm text-muted-foreground pt-2 flex items-center gap-3">
            <span>{date}</span>
            {readingTime > 0 && <span data-testid="reading-time">· {readingTime} min de lecture</span>}
          </div>
        </div>
      </Link>
    </article>
  );
}
